/**
 * App shell & router. Route-level code splitting keeps the hub LCP small
 * (FE architecture doc); studios lazy-load.
 */
import { lazy, Suspense, type ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { createBrowserRouter, Link, Outlet, RouterProvider } from 'react-router';
import { PromoBanner } from './components/commerce';
import ErrorBoundary from './components/ErrorBoundary';
import { Navbar } from './components/Navbar';
import { Skeleton } from './components/ui';
import { AuthProvider, useAuth } from './lib/auth';
import { useRealtime } from './lib/ws';

const HomePage = lazy(() => import('./pages/HomePage'));
const CreatePage = lazy(() => import('./pages/CreatePage'));
const UpscalePage = lazy(() => import('./pages/UpscalePage'));
const InpaintPage = lazy(() => import('./pages/InpaintPage'));
const CharacterSwapPage = lazy(() => import('./pages/CharacterSwapPage'));
const EditVideoPage = lazy(() => import('./pages/EditVideoPage'));
const LipsyncPage = lazy(() => import('./pages/LipsyncPage'));
const LibraryPage = lazy(() => import('./pages/LibraryPage'));
const ExplorePage = lazy(() => import('./pages/ExplorePage'));
const TemplatesPage = lazy(() => import('./pages/TemplatesPage'));
const TemplateDetailPage = lazy(() => import('./pages/TemplateDetailPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const AuthPage = lazy(() => import('./pages/AuthPage'));
const AccountPage = lazy(() => import('./pages/AccountPage'));
const StatusPage = lazy(() => import('./pages/StatusPage'));
const ComingSoonPage = lazy(() => import('./pages/ComingSoonPage'));
const MarketingStudioPage = lazy(() => import('./pages/MarketingStudioPage'));
const McpPage = lazy(() => import('./pages/McpPage'));
const CharactersPage = lazy(() => import('./pages/CharactersPage'));
const CinemaStudioPage = lazy(() => import('./pages/CinemaStudioPage'));
const FacelessPage = lazy(() => import('./pages/FacelessPage'));
const SupercomputerPage = lazy(() => import('./pages/SupercomputerPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const TermsPage = lazy(() => import('./pages/legal/TermsPage'));
const PrivacyPage = lazy(() => import('./pages/legal/PrivacyPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 60_000, retry: 1, refetchOnWindowFocus: false } },
});

function PageFallback() {
  return (
    <div className="mx-auto max-w-[1500px] space-y-4 p-6">
      <Skeleton className="h-10 w-64" />
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        {Array.from({ length: 8 }, (_, i) => (
          <Skeleton key={i} className="aspect-[4/5]" />
        ))}
      </div>
    </div>
  );
}

function Footer() {
  const { t } = useTranslation();
  return (
    <footer className="mt-16 border-t border-border">
      <div className="mx-auto flex max-w-[1500px] flex-col items-center gap-3 px-4 py-8 text-center">
        <p className="text-xs text-muted">{t('common.poweredBy')}</p>
        <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-muted">
          <Link to="/status" className="hover:text-text">{t('nav.status')}</Link>
          <Link to="/pricing" className="hover:text-text">{t('nav.pricing')}</Link>
          <Link to="/explore" className="hover:text-text">{t('nav.explore')}</Link>
          <Link to="/legal/terms" className="hover:text-text">{t('footer.terms')}</Link>
          <Link to="/legal/privacy" className="hover:text-text">{t('footer.privacy')}</Link>
          <a href="mailto:hello@pixellars.com" className="hover:text-text">{t('footer.contact')}</a>
        </div>
      </div>
    </footer>
  );
}

function Shell() {
  const { user } = useAuth();
  useRealtime(!!user); // one multiplexed socket per signed-in session
  return (
    <div className="flex min-h-dvh flex-col">
      <PromoBanner />
      <Navbar />
      <main className="flex-1">
        <ErrorBoundary>
          <Suspense fallback={<PageFallback />}>
            <Outlet />
          </Suspense>
        </ErrorBoundary>
      </main>
      <Footer />
    </div>
  );
}

function NotFound() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center gap-4 py-24">
      <p className="display text-5xl">404</p>
      <p className="text-muted">{t('common.notFound')}</p>
      <Link to="/" className="text-accent hover:underline">
        {t('common.goHome')}
      </Link>
    </div>
  );
}

const router = createBrowserRouter([
  {
    element: <Shell />,
    children: [
      { path: '/', element: <HomePage /> },
      { path: '/create/:modality', element: <CreatePage /> },
      { path: '/tools/upscale', element: <UpscalePage /> },
      { path: '/tools/inpaint', element: <InpaintPage /> },
      { path: '/tools/character-swap', element: <CharacterSwapPage /> },
      { path: '/tools/edit-video', element: <EditVideoPage /> },
      { path: '/tools/lipsync', element: <LipsyncPage /> },
      { path: '/studio/marketing', element: <MarketingStudioPage /> },
      { path: '/studio/marketing/:projectId', element: <MarketingStudioPage /> },
      { path: '/mcp', element: <McpPage /> },
      { path: '/characters', element: <CharactersPage /> },
      { path: '/studio/cinema', element: <CinemaStudioPage /> },
      { path: '/studio/faceless', element: <FacelessPage /> },
      { path: '/supercomputer', element: <SupercomputerPage /> },
      { path: '/library', element: <LibraryPage /> },
      { path: '/explore', element: <ExplorePage /> },
      { path: '/explore/:id', element: <ExplorePage /> },
      { path: '/templates', element: <TemplatesPage /> },
      { path: '/templates/:id', element: <TemplateDetailPage /> },
      { path: '/pricing', element: <PricingPage /> },
      { path: '/login', element: <AuthPage mode="login" /> },
      { path: '/signup', element: <AuthPage mode="signup" /> },
      { path: '/forgot-password', element: <ForgotPasswordPage /> },
      { path: '/reset-password', element: <ResetPasswordPage /> },
      { path: '/verify-email', element: <VerifyEmailPage /> },
      { path: '/legal/terms', element: <TermsPage /> },
      { path: '/legal/privacy', element: <PrivacyPage /> },
      { path: '/admin', element: <AdminPage /> },
      { path: '/account', element: <AccountPage /> },
      { path: '/status', element: <StatusPage /> },
      { path: '/soon/:feature', element: <ComingSoonPage /> },
      { path: '*', element: <NotFound /> },
    ],
  },
]);

export default function App(): ReactNode {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <RouterProvider router={router} />
      </AuthProvider>
    </QueryClientProvider>
  );
}
